import { AppRoutes } from '@/config/routes'
import { useIsSpaceRoute } from '@/hooks/useIsSpaceRoute'
import { usePathname } from 'next/navigation'
import { useRouter } from 'next/router'

const NO_SIDEBAR_ROUTES = [
  AppRoutes.share.safeApp,
  AppRoutes.newSafe.create,
  AppRoutes.newSafe.load,
  AppRoutes.index,
  AppRoutes.welcome.index,
  AppRoutes.welcome.accounts,
  AppRoutes.welcome.spaces,
  AppRoutes.imprint,
  AppRoutes.privacy,
  AppRoutes.cookie,
  AppRoutes.terms,
  AppRoutes.licenses,
]

const TOGGLE_SIDEBAR_ROUTES = [AppRoutes.apps.open]

const hasSafeQueryParam = (asPath?: string): boolean => {
  if (!asPath) return false
  const queryStart = asPath.indexOf('?')
  if (queryStart === -1) return false
  const query = asPath.slice(queryStart + 1).split('#')[0]
  return new URLSearchParams(query).has('safe')
}

/**
 * Returns a boolean tuple indicating if the current route should display the sidebar and if the sidebar can be toggled
 * @param pathname Optional server-side pathname to check against
 * @returns A tuple with the first value indicating if the sidebar should be displayed and the second value indicating if the sidebar can be toggled
 */
export function useIsSidebarRoute(pathname?: string): [boolean, boolean] {
  const router = useRouter()
  const clientPathname = usePathname()
  const isSpaceRoute = useIsSpaceRoute(pathname)
  const route = pathname || clientPathname || ''
  const isReady = router.isReady
  const sidebarQuery = isReady && router.query.sidebar === 'true'
  const noSidebar = NO_SIDEBAR_ROUTES.includes(route) && !sidebarQuery
  const toggledSidebar = TOGGLE_SIDEBAR_ROUTES.includes(route) && !sidebarQuery
  const hasSafe = isReady && (Boolean(router.query.safe) || hasSafeQueryParam(router.asPath))

  const displaySidebar = (!noSidebar && hasSafe) || isSpaceRoute

  return [displaySidebar, toggledSidebar]
}
